import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as FboLocationsActions from './fbo-locations.actions';
import { ToastService } from 'src/app/core/services/toast.service';
import { FboLocationApiService } from '@core/services/fbo-location-api.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { PERMISSIONS } from 'src/app/core/constants/permissions.constants';
import { GetFboLocationsResponse } from 'src/app/api/administration/models/get-fbo-locations-response';
import { FboLocationResponse } from 'src/app/api/administration/models/fbo-location-response';

@Injectable()
export class FboLocationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly fboLocationApiService = inject(FboLocationApiService);
  private readonly toastService = inject(ToastService);
  private readonly permissionService = inject(PermissionService);

  loadFboLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FboLocationsActions.loadFboLocations),
      switchMap(() =>
        this.fboLocationApiService.getCurrentUserFboLocations().pipe(
          map((response) => {
            const locations = response.data || [];
            return FboLocationsActions.loadFboLocationsSuccess({ locations });
          }),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Failed to load locations';
            return of(
              FboLocationsActions.loadFboLocationsFailure({
                error: errorMessage,
              })
            );
          })
        )
      )
    )
  );

  loadFboLocationsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FboLocationsActions.loadFboLocationsFailure),
        tap(({ error }) => {
          this.toastService.showError('Failed to load locations', error);
        })
      ),
    { dispatch: false }
  );

  switchFboLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FboLocationsActions.switchFboLocation),
      switchMap(({ locationId }) =>
        this.fboLocationApiService.switchFboLocation(locationId).pipe(
          map((response) => {
            // Get the selected location from the response or build it from the ID
            const locationDataArray = response.data || [];
            const locationData = Array.isArray(locationDataArray)
              ? locationDataArray[0]
              : locationDataArray;

            const selectedLocation = {
              id: locationId,
              name: locationData?.name || `Location ${locationId}`,
              description: locationData?.description,
              enabled: locationData?.enabled,
              active: true,
            };
            return FboLocationsActions.switchFboLocationSuccess({ selectedLocation });
          }),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Failed to switch location';
            return of(
              FboLocationsActions.switchFboLocationFailure({
                error: errorMessage,
              })
            );
          })
        )
      )
    )
  );

  switchFboLocationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FboLocationsActions.switchFboLocationSuccess),
        tap(({ selectedLocation }) => {
          this.toastService.showSuccess(
            'Location Switched',
            `Switched to ${selectedLocation.name || 'the selected location'}`
          );
        })
      ),
    { dispatch: false }
  );

  switchFboLocationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FboLocationsActions.switchFboLocationFailure),
        tap(({ error }) => {
          this.toastService.showError('Failed to switch location', error);
        })
      ),
    { dispatch: false }
  );

  loadAllFboLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FboLocationsActions.loadAllFboLocations),
      switchMap(({ params }) =>
        this.fboLocationApiService.getAllFboLocations(params).pipe(
          map((response: GetFboLocationsResponse) => {
            const locations = response.data || [];
            const totalRecords = response.page?.totalElements || locations.length;
            return FboLocationsActions.loadAllFboLocationsSuccess({
              locations,
              totalRecords,
            });
          }),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Failed to load all locations';
            return of(
              FboLocationsActions.loadAllFboLocationsFailure({
                error: errorMessage,
              })
            );
          })
        )
      )
    )
  );

  loadAllFboLocationsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FboLocationsActions.loadAllFboLocationsFailure),
        tap(({ error }) => {
          this.toastService.showError('Failed to load all locations', error);
        })
      ),
    { dispatch: false }
  );

  createFboLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FboLocationsActions.createFboLocation),
      switchMap(({ fboLocation }) => {
        // Check permission before calling API
        if (!this.permissionService.hasPermission(PERMISSIONS.GSE.FBO_LOCATION.CREATE)) {
          return of(
            FboLocationsActions.createFboLocationFailure({
              error: 'You do not have permission to create FBO locations',
            })
          );
        }
        return this.fboLocationApiService.createFboLocation(fboLocation).pipe(
          map((response: FboLocationResponse) => {
            const location = response.data?.[0];
            if (!location) {
              throw new Error('Failed to create location');
            }
            return FboLocationsActions.createFboLocationSuccess({ location });
          }),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Failed to create location';
            return of(
              FboLocationsActions.createFboLocationFailure({
                error: errorMessage,
              })
            );
          })
        );
      })
    )
  );

  createFboLocationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FboLocationsActions.createFboLocationSuccess),
      tap(() => {
        this.toastService.showSuccess('Location Created', 'FBO location created successfully');
      }),
      map(() => {
        return FboLocationsActions.loadAllFboLocations({
          params: { page: 0, size: 10, sort: 'createdAt,desc' },
        });
      })
    )
  );

  createFboLocationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FboLocationsActions.createFboLocationFailure),
        tap(({ error }) => {
          this.toastService.showError('Failed to create location', error);
        })
      ),
    { dispatch: false }
  );

  getFboLocationDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FboLocationsActions.getFboLocationDetails),
      switchMap(({ locationId }) =>
        this.fboLocationApiService.getFboLocationDetails(locationId).pipe(
          map((response: FboLocationResponse) => {
            // Response structure: { data: [{ accesses: [...], description, id, name }] }
            const locationData = response.data?.[0];
            if (!locationData) {
              throw new Error('Failed to load location details');
            }
            return FboLocationsActions.getFboLocationDetailsSuccess({ location: locationData });
          }),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Failed to load location details';
            return of(
              FboLocationsActions.getFboLocationDetailsFailure({
                error: errorMessage,
              })
            );
          })
        )
      )
    )
  );

  getFboLocationDetailsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FboLocationsActions.getFboLocationDetailsFailure),
        tap(({ error }) => {
          this.toastService.showError('Failed to load location details', error);
        })
      ),
    { dispatch: false }
  );

  updateFboLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FboLocationsActions.updateFboLocation),
      switchMap(({ locationId, fboLocation }) => {
        // Check permission before calling API
        if (!this.permissionService.hasPermission(PERMISSIONS.GSE.FBO_LOCATION.UPDATE)) {
          return of(
            FboLocationsActions.updateFboLocationFailure({
              error: 'You do not have permission to update FBO locations',
            })
          );
        }
        return this.fboLocationApiService.updateFboLocation(locationId, fboLocation).pipe(
          map((response: FboLocationResponse) => {
            const location = response.data?.[0];
            if (!location) {
              throw new Error('Failed to update location');
            }
            return FboLocationsActions.updateFboLocationSuccess({ location });
          }),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Failed to update location';
            return of(
              FboLocationsActions.updateFboLocationFailure({
                error: errorMessage,
              })
            );
          })
        );
      })
    )
  );

  updateFboLocationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FboLocationsActions.updateFboLocationSuccess),
      tap(() => {
        this.toastService.showSuccess('Location Updated', 'FBO location updated successfully');
      }),
      map(() => {
        return FboLocationsActions.loadAllFboLocations({
          params: { page: 0, size: 10, sort: 'createdAt,desc' },
        });
      })
    )
  );

  updateFboLocationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FboLocationsActions.updateFboLocationFailure),
        tap(({ error }) => {
          this.toastService.showError('Failed to update location', error);
        })
      ),
    { dispatch: false }
  );

  deleteFboLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FboLocationsActions.deleteFboLocation),
      switchMap(({ locationId }) => {
        // Check permission before calling API
        if (!this.permissionService.hasPermission(PERMISSIONS.GSE.FBO_LOCATION.DELETE)) {
          return of(
            FboLocationsActions.deleteFboLocationFailure({
              error: 'You do not have permission to delete FBO locations',
            })
          );
        }
        return this.fboLocationApiService.deleteFboLocation(locationId).pipe(
          map(() => FboLocationsActions.deleteFboLocationSuccess({ locationId })),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Failed to delete location';
            return of(
              FboLocationsActions.deleteFboLocationFailure({
                error: errorMessage,
              })
            );
          })
        );
      })
    )
  );

  deleteFboLocationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FboLocationsActions.deleteFboLocationSuccess),
      tap(() => {
        this.toastService.showSuccess('Location Deleted', 'FBO location deleted successfully');
      }),
      map(() => {
        return FboLocationsActions.loadAllFboLocations({
          params: { page: 0, size: 10, sort: 'createdAt,desc' },
        });
      })
    )
  );

  deleteFboLocationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FboLocationsActions.deleteFboLocationFailure),
        tap(({ error }) => {
          this.toastService.showError('Failed to delete location', error);
        })
      ),
    { dispatch: false }
  );
}
